import React, {
  useState,
  useMemo,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import {
  HiChevronDown,
  HiChevronUp,
  HiChevronUpDown,
} from "react-icons/hi2";
import { Button } from "./Button";

export interface Column<T> {
  key: keyof T | "action";
  label: string;
  sortable?: boolean;
  hideSortIcon?: boolean;
  customCell?: (row: T) => React.ReactNode;
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  totalCount: number;
  tableName?: string;
  onSearchChange: (search: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  currentPage: number;
  pageSize: number;
  loading?: boolean;
  isSelectable?: boolean; 
  onSelectionChange?: (selectedRows: T[]) => void;
  rowKey?: keyof T | ((row: T) => string | number);
  renderLeftToolItems?: React.ReactNode;
  renderRightToolItems?: React.ReactNode;
  pageSizeOptions?: number[];
  filterContent?: any;
  handleFilter?: any;
  handleClear?: any;
  onSortChange?: (sortBy: keyof T | null, sortOrder: SortDirection) => void;
}

type SortDirection = "asc" | "desc" | null;

export interface TableHandle {
  closeFilterDrawer: () => void;
  clearSelection: () => void;
}

const Table = forwardRef(<T extends Record<string, any>>(
  {
    columns,
    data,
    totalCount,
    tableName,
    onSearchChange,
    onPageChange,
    onPageSizeChange,
    currentPage,
    pageSize,
    loading = false,
    isSelectable = false,
    onSelectionChange,
    rowKey = "id" as keyof T,
    pageSizeOptions = [5, 10, 20, 50],
    filterContent,
    renderLeftToolItems,
    renderRightToolItems,
    handleFilter,
    handleClear,
    onSortChange,
  }: TableProps<T>,
  ref: React.Ref<TableHandle>
) => {
  const [sortConfig, setSortConfig] = useState<{
    key: keyof T;
    direction: SortDirection;
  } | null>(null);

  const [searchValue, setSearchValue] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string | number>>(new Set());

  useImperativeHandle(ref, () => ({
    closeFilterDrawer() {
      setShowFilter(false);
    },
    clearSelection() {
      setSelectedKeys(new Set());
      if (onSelectionChange) onSelectionChange([]);
    }
  }));

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onSearchChange(searchValue.trim());
    }
  };

  const onSort = (column: Column<T>) => {
    if (!column.sortable) return;

    setSortConfig((prev) => {
      let newSortConfig: { key: keyof T; direction: SortDirection } | null = null;
      if (!prev || prev.key !== column.key) {
        newSortConfig = { key: column.key, direction: "asc" as SortDirection };
      } else if (prev.direction === "asc") {
        newSortConfig = { key: column.key, direction: "desc" as SortDirection };
      } else if (prev.direction === "desc") {
        newSortConfig = null;
      } else {
        newSortConfig = { key: column.key, direction: "asc" as SortDirection };
      }
      if (onSortChange) {
        onSortChange(newSortConfig?.key ?? null, newSortConfig?.direction ?? null);
      }
      return newSortConfig;
    });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig) return data;
    const { key, direction } = sortConfig;
    return [...data].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === "number" && typeof bVal === "number") {
        return direction === "asc" ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return direction === "asc" ? -1 : 1;
      if (aStr > bStr) return direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  const getRowKey = (row: T, index: number) => {
    if (typeof rowKey === "function") return rowKey(row);
    if (row[rowKey as keyof T] !== undefined) return row[rowKey as keyof T] as unknown as string | number;
    return row._id || row.id || index;
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    const newKeys = new Set(selectedKeys);
    sortedData.forEach((row, idx) => {
      const key = getRowKey(row, idx);
      if (isChecked) {
        newKeys.add(key);
      } else {
        newKeys.delete(key);
      }
    });
    setSelectedKeys(newKeys);
    if (onSelectionChange) {
      const selectedRows = sortedData.filter((row, idx) => newKeys.has(getRowKey(row, idx)));
      onSelectionChange(selectedRows);
    }
  };

  const handleSelectRow = (row: T, index: number, isChecked: boolean) => {
    const key = getRowKey(row, index);
    const newKeys = new Set(selectedKeys);
    if (isChecked) {
      newKeys.add(key);
    } else {
      newKeys.delete(key);
    }
    setSelectedKeys(newKeys);
    if (onSelectionChange) {
      const selectedRows = sortedData.filter((r, idx) => newKeys.has(getRowKey(r, idx)));
      onSelectionChange(selectedRows);
    }
  };

  const isAllSelected = sortedData.length > 0 && sortedData.every((row, idx) => selectedKeys.has(getRowKey(row, idx)));
  const isSomeSelected = sortedData.some((row, idx) => selectedKeys.has(getRowKey(row, idx)));
  const totalPages = Math.ceil(totalCount / pageSize);

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    onPageChange(page);
  };

  const handleOpenFilter = () => setShowFilter(true);
  const handleCloseFilter = () => setShowFilter(false);
  const totalColSpan = isSelectable ? columns.length + 1 : columns.length;

  return (
    <div className="bg-white shadow rounded border border-gray-200 flex flex-col min-h-[50vh] max-h-[90vh] relative overflow-hidden">
      {/* Toolbar */}
      <div className="flex justify-between items-center p-4 border-b border-gray-200">
        <div className="flex gap-4 items-center">
          <div>
            <div className="text-md text-gray-600 font-medium">{tableName}</div>
            <div className="text-sm text-gray-600 font-medium">
              Showing {data.length} of {totalCount} entries
            </div>
            {isSelectable && selectedKeys.size > 0 && (
               <div className="text-sm text-blue-600 font-medium mt-1">
                 {selectedKeys.size} selected
               </div>
            )}
          </div>
          <div>{renderLeftToolItems}</div>
        </div>
        <div className="flex gap-4 items-center">
          <div>{renderRightToolItems}</div>
          <div className="flex items-center space-x-3">
            <input
              type="search"
              placeholder="Search..."
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value)                
                if(!e.target.value) onSearchChange("");
              }}
              onBlur={() => onSearchChange(searchValue)}
              className="w-full max-w-xs rounded border border-gray-300 px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-600 focus:outline-none"
              aria-label="Search table"
              onKeyDown={onSearchKeyDown}
            />
            {filterContent && (
              <Button onClick={handleOpenFilter}>
                Filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable table body */}
      <div className="overflow-y-auto flex-grow relative">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              {isSelectable && (
                <th scope="col" className="px-4 py-2 w-12 text-center border-b border-gray-200">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    checked={isAllSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = !isAllSelected && isSomeSelected;
                    }}
                    onChange={handleSelectAll}
                  />
                </th>
              )}

              {columns.map(({ key, label, sortable, hideSortIcon }) => {
                const isSorted = sortConfig?.key === key;
                const direction = sortConfig?.direction;
                return (
                  <th
                    key={String(key)}
                    scope="col"
                    onClick={() => onSort({ key, label, sortable, hideSortIcon })}
                    className={`px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider select-none border-b border-gray-200 ${!sortable ? "" : "cursor-pointer hover:text-blue-600"}`}
                    aria-sort={isSorted ? (direction === "asc" ? "ascending" : "descending") : "none"}
                  >
                    <div className="text-black flex items-center space-x-1">
                      <span>{label}</span>
                      {sortable && !hideSortIcon && <SortIcon direction={isSorted ? direction : null} />}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={totalColSpan} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center justify-center space-y-3">
                    <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-gray-500 font-medium">Loading data...</span>
                  </div>
                </td>
              </tr>
            ) : sortedData?.length === 0 ? (
              <tr>
                <td colSpan={totalColSpan} className="px-4 py-8 text-center text-gray-400">
                  No records found.
                </td>
              </tr>
            ) : (
              sortedData?.map((row, idx) => {
                const isSelected = selectedKeys.has(getRowKey(row, idx));
                return (
                  <tr key={idx} className={`${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} ${isSelected ? 'bg-blue-50/40' : ''}`}>
                    {isSelectable && (
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          checked={isSelected}
                          onChange={(e) => handleSelectRow(row, idx, e.target.checked)}
                        />
                      </td>
                    )}
                    {columns.map(({ key, customCell }) => (
                      <td key={String(key)} className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                        {customCell ? customCell(row) : row[key] != null ? String(row[key]) : ""}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center p-4 border-t border-gray-200">
        <div className="flex items-center space-x-2">
          <label htmlFor="pageSize" className="text-sm text-gray-700">Rows per page:</label>
          <select
            id="pageSize"
            value={pageSize}
            disabled={loading}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="rounded border border-gray-300 px-2 py-1 text-sm disabled:opacity-50"
          >
            {pageSizeOptions?.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center space-x-1">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1 || loading}
            className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            &lt;
          </button>
          <span className="text-sm font-medium px-2">Page {currentPage} of {totalPages || 1}</span>
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= (totalPages || 1) || loading}
            className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            &gt;
          </button>
        </div>
      </div>

      {/* ✅ SMOOTH ANIMATED FILTER DRAWER */}
      {filterContent && (
        <>
          {/* Backdrop Fade Animation */}
          <div
            className={`fixed inset-0 bg-black transition-opacity duration-300 ease-in-out z-40 ${
              showFilter ? "opacity-40 pointer-events-auto" : "opacity-0 pointer-events-none"
            }`}
            onClick={handleCloseFilter}
            aria-hidden={!showFilter}
          />

          {/* Drawer Slide Animation */}
          <div
            className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl border-l border-gray-200 flex flex-col transform transition-transform duration-300 ease-in-out z-50 ${
              showFilter ? "translate-x-0" : "translate-x-full"
            }`}
            role="dialog"
            aria-modal={showFilter}
            aria-label="Filter drawer"
            onClick={(e) => e.stopPropagation()} 
          >
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg font-semibold">Filters</h2>
              <button
                onClick={handleCloseFilter}
                className="text-gray-500 hover:text-gray-700 transition-colors"
                aria-label="Close filters"
              >
                ✕
              </button>
            </div>

            {/* Filter Content */}
            {filterContent}
          </div>
        </>
      )}
    </div>
  );
});

function SortIcon({ direction }: { direction: SortDirection | any }) {
  return direction === "asc" ? <HiChevronUp /> : direction === "desc" ? <HiChevronDown /> : <HiChevronUpDown />;
}

export default Table;